import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebase/firebase-config';
import { t } from '../lib/i18n';
// SOLUCIÓN: Se corrige la ruta para apuntar a la carpeta 'components'
import TransactionModal from '../components/TransactionModal';

// ============================================================================
// VISTA: TransactionsView (v58.5 - Rediseñado como Modal y Corregido)
// ============================================================================
const TransactionsView = ({ material, db, user, onClose }) => {
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [revertingId, setRevertingId] = useState(null);
    // Revertir movimientos viejos = acción de ADMIN (canManageMaterials), oculta al obrero.
    // El obrero corrige conteos con el ajuste absoluto del StockEntrySheet, no revirtiendo.
    const canRevert = user?.permissions?.canManageMaterials;

    // Revertir un movimiento (cantidad ingresada por error): NO borra el original;
    // postea un ajuste compensatorio vía Cloud Function (audit-safe).
    const handleRevert = async (tx) => {
        const signed = String(tx.type || '').includes('Entrada') ? Number(tx.quantity) : -Number(tx.quantity);
        if (!Number.isFinite(signed) || signed === 0) return;
        if (!window.confirm(t('correct.revertConfirm'))) return;
        setRevertingId(tx.id);
        try {
            const fn = httpsCallable(functions, 'adjustStock');
            await fn({ materialId: material.id, delta: -signed, reason: `Reversa de movimiento ${tx.id}`, correctsTransactionId: tx.id });
            await fetchTransactions();
        } catch (e) {
            console.error('adjustStock (revertir) falló:', e);
            alert(e.message || t('correct.error'));
        } finally {
            setRevertingId(null);
        }
    };

    const fetchTransactions = useCallback(async () => {
        if (!material) return;
        setIsLoading(true);
        try {
            const transactionsRef = collection(db, "materials", material.id, "transactions");
            const q = query(transactionsRef, orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);
            const transData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                date: doc.data().timestamp?.toDate() // Añadir ? para seguridad
            }));
            setTransactions(transData);
        } catch (error) {
            console.error("Error cargando transacciones:", error);
        } finally {
            setIsLoading(false);
        }
    }, [db, material]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // Slice 4: el movimiento manual ya NO se escribe desde el cliente. Va por la CF
    // `adjustStock` (admin SDK + permiso + ITC, audit-safe: postea un movimiento
    // compensatorio con el signo correcto y guarda contra stock negativo).
    const handleSaveTransaction = async (materialId, transactionData) => {
        try {
            const signed = String(transactionData.type || '').includes('Entrada')
                ? Number(transactionData.quantity) : -Number(transactionData.quantity);
            if (!Number.isFinite(signed) || signed === 0) { setIsModalOpen(false); return; }
            const reason = [transactionData.notes, transactionData.jobId ? `job ${transactionData.jobId}` : '']
                .filter(Boolean).join(' · ') || transactionData.type;
            const fn = httpsCallable(functions, 'adjustStock');
            await fn({ materialId, delta: signed, reason });
            setIsModalOpen(false);
            fetchTransactions();
        } catch (error) {
            console.error("Error al guardar la transacción:", error);
            alert(`Error: ${error.message}`);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Pendiente...';
        return date.toLocaleString('es-ES', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
                <div className="flex-shrink-0 p-6 border-b flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Historial de Movimientos</h2>
                        <p className="text-gray-600">Material: <span className="font-semibold">{material.name}</span></p>
                    </div>
                    <div className="flex gap-4">
                        <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                            + Nuevo Movimiento
                        </button>
                        <button onClick={onClose} className="px-4 py-2 font-bold text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                            Cerrar
                        </button>
                    </div>
                </div>
                
                <div className="flex-grow overflow-auto">
                    {isLoading ? <p className="text-center p-10">Cargando historial...</p> : (
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-6 py-3">Fecha</th>
                                    <th className="px-6 py-3">Tipo</th>
                                    <th className="px-6 py-3 text-center">Cantidad</th>
                                    <th className="px-6 py-3">Job ID</th>
                                    <th className="px-6 py-3">Usuario</th>
                                    <th className="px-6 py-3">Notas</th>
                                    {canRevert && <th className="px-6 py-3 text-center">{t('remnant.col.actions')}</th>}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {transactions.map(tx => {
                                    const isEntrada = String(tx.type).includes('Entrada');
                                    // No tiene sentido revertir un ajuste que ya es una reversa (evita cadenas confusas).
                                    const isReversal = !!tx.correctsTransactionId;
                                    return (
                                    <tr key={tx.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(tx.date)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isEntrada ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {tx.type}
                                            </span>
                                        </td>
                                        <td className={`px-6 py-4 text-center font-bold text-lg ${isEntrada ? 'text-green-600' : 'text-red-600'}`}>
                                            {isEntrada ? `+${tx.quantity}` : `-${tx.quantity}`}
                                        </td>
                                        <td className="px-6 py-4 font-mono">{tx.jobId || 'N/A'}</td>
                                        <td className="px-6 py-4 text-xs">{tx.userEmail}</td>
                                        <td className="px-6 py-4 text-xs max-w-sm truncate" title={tx.notes}>{tx.notes}</td>
                                        {canRevert && (
                                            <td className="px-6 py-4 text-center">
                                                {!isReversal && (
                                                    <button onClick={() => handleRevert(tx)} disabled={revertingId === tx.id}
                                                        className="px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded hover:bg-amber-100 disabled:opacity-40">
                                                        {revertingId === tx.id ? '…' : t('correct.revert')}
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <TransactionModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveTransaction}
                    material={material}
                    db={db}
                    user={user}
                />
            </div>
        </div>
    );
};

export default TransactionsView;
